import { PageWrapper, useLayout } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { hocs as pageHocs } from 'modules/page/hocs';
import { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';
import { HelpStripe } from 'shared/components';

import { ReportCreatePopup, ReportsListTiles, ReportsListToolbar } from '../resources/components';
import { useCreateReportPopup } from '../resources/components/ReportCreatePopup/ReportCreatePopup.hooks';
import { pageConfig } from './config';
import { Loading } from './loading';
import { useBreadcrumbs } from './resources/hooks/useBreadcrumbs';
import { useGetHelpId } from './resources/hooks/useGetHelpId';
import { usePageData } from './resources/hooks/usePageData';
import { messages } from './resources/messages';
import { ContentProps } from './resources/types';

const Content = memo<ContentProps>((props) => {
    const { company } = props;

    useBreadcrumbs(company);

    useMount(() => {
        amplitudeService.sendData('reports: view list');
    });

    const helpId = useGetHelpId(company);
    const createReportPopup = useCreateReportPopup();

    return (
        <PageWrapper data-qa='reports-page'>
            <Helmet title={messages.title({ companyName: company.displayName })} />

            <PageWrapper.Toolbar>
                <ReportsListToolbar helpStripeId={helpId} />

                <HelpStripe id={helpId} />
            </PageWrapper.Toolbar>

            <PageWrapper.Content>
                <ReportsListTiles />

                {createReportPopup.isOpen && <ReportCreatePopup />}
            </PageWrapper.Content>
        </PageWrapper>
    );
});

export const Page = memo(() => {
    const activeInnerPageId = useSelector(selectors.navigation.getInnerActivePageId);

    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading, company } = usePageData();

    return isInitialLoading || !company || activeInnerPageId !== pageConfig.innerActivePageId ? (
        <Loading />
    ) : (
        <Content company={company} />
    );
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
